import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ShfyRoles } from 'src/app/appsettings';
import { ClaimTypesEnum } from 'src/app/plasma-ui-common/enums/claim-types.enum';
import { WhiteLabelNames } from 'src/app/plasma-ui-common/enums/white-label.enum';
import { ConfigService } from "../config/config.service";
import { RegisterProfileApiService } from "../register-profile-api/register-profile-api.service";
import { AuthService } from "../auth/auth.service";
import { take } from "rxjs/operators";
import { UserTypes } from 'src/app/plasma-ui-common/enums/user-type.enum';
import { ClaimValue } from 'src/app/plasma-ui-common/enums/claim-value.enum';

@Injectable({
  providedIn: "root",
})
export class PreventShfyGuard {
  constructor(
    private router: Router,
    private auth: AuthService,
    private config: ConfigService,
    private registerProfileApiService: RegisterProfileApiService
  ) {}

  private getConfig(claim: string): string {
    return String(this.config._envConfig[claim]);
  }

  private getUrlParam(param: string) {
    return new URLSearchParams(window.location.search).get(param);
  }

  private navigateToDxp(isTwoFAEnabled: boolean, isActive: boolean, userId: string, email: string) {
    this.router.navigateByUrl("/404");
    if (isTwoFAEnabled) {
      this.navigateToUrl(this.config.shfyDxpBaseURL + "/my/settings");
    } else if (!isActive) {
      this.navigateToUrl(this.config.shfyRegistrationURL + `?userId=${userId}&email=${email}`);
    } else {
      this.navigateToUrl(this.config.shfyCompleteProfileURL);
    }
  }

  private navigateToUrl(url: string) {
    window.location.href = url;
  }

  private async isLoggedInUserAllowed() {
    let isAllowed = true;
    this.auth.userInfo$.pipe(take(1)).subscribe((info) => {
      const infoUserTypes = info[ClaimTypesEnum.USER_TYPE];
      const isTwoFAEnabled = info[ClaimTypesEnum.TWO_FACTOR_ENABLED].toLowerCase() === ClaimValue.TRUE;
      const isEmailVerified = info[ClaimTypesEnum.EMAIL_VERIFIED].toLowerCase() === ClaimValue.TRUE;
      const shfyRoles: string[] = (info[ClaimTypesEnum.SHFY_ROLES] || "").toLowerCase().split(",");
      const isActive = info[ClaimTypesEnum.ACTIVE].toLowerCase() === ClaimValue.TRUE;
      const email = info[ClaimTypesEnum.EMAIL];
      const userId = info[ClaimTypesEnum.USERID];
      const isShfyDoctor = shfyRoles.includes(ShfyRoles.DOCTOR);

      if (!isShfyDoctor) {
        if (Array.isArray(infoUserTypes)) {
          infoUserTypes.forEach((type) => {
            if (type === UserTypes.SYNLAB_ACCESS_USER) {
              this.navigateToDxp(isTwoFAEnabled, isEmailVerified && isActive, userId, email);
              isAllowed = false;
            }
          });
        } else {
          if (infoUserTypes === UserTypes.SYNLAB_ACCESS_USER) {
            this.navigateToDxp(isTwoFAEnabled, isEmailVerified && isActive, userId, email);
            isAllowed = false;
          }
        }
      }
    });
    return isAllowed;
  }

  private async isGuestUserAllowed(id: string, seqId: string) {
    let isAllowed = true;
    let isTwoFAEnabled = false;
    let isActive = false;
    let isEmailVerified = false;
    let userId = "";
    let email = "";

    const claims = await this.registerProfileApiService.getRegisterProfile(id, seqId).toPromise();

    claims.forEach((claim) => {
      if (claim.claimType === ClaimTypesEnum.USER_TYPE && claim.claimValue === UserTypes.SYNLAB_ACCESS_USER)
        isAllowed = false;
      if (claim.claimType === ClaimTypesEnum.TWO_FACTOR_ENABLED && claim.claimValue.toLowerCase() === ClaimValue.TRUE)
        isTwoFAEnabled = true;
      if (claim.claimType === ClaimTypesEnum.EMAIL_VERIFIED && claim.claimValue.toLowerCase() === ClaimValue.TRUE)
        isEmailVerified = true;
      if (claim.claimType === ClaimTypesEnum.ACTIVE && claim.claimValue.toLowerCase() === ClaimValue.TRUE) isActive = true;
      if (claim.claimType === ClaimTypesEnum.EMAIL) email = claim.claimValue;
      if (claim.claimType === ClaimTypesEnum.USERID) userId = claim.claimValue;
    });

    if (!isAllowed) this.navigateToDxp(isTwoFAEnabled, isEmailVerified && isActive, userId, email);

    return isAllowed;
  }

  canActivate(): any {
    const isWhiteLabeled = this.getConfig(ClaimTypesEnum.WHITE_LABEL_ENABLED).toLowerCase() === "true";
    const whiteLabelName = this.getConfig(ClaimTypesEnum.WHITE_LABEL_NAME);
    if (isWhiteLabeled && whiteLabelName === WhiteLabelNames.UK_SHFY) {
      let seqId = this.getUrlParam("sequenceId") || this.getUrlParam("seqId");
      let id = this.getUrlParam("id");

      if (seqId && id) {
        return this.isGuestUserAllowed(id, seqId);
      } else {
        return this.isLoggedInUserAllowed();
      }
    }
    return true;
  }
}
